import { BaseService } from '../base/BaseService';
import type { ServiceResponse } from '../base/ServiceTypes';

export interface AISuggestion {
  id: string;
  brand_kit_id?: string | null;
  topic: string;
  description: string | null;
  goal: string | null;
  suggested_products: string[];
  is_used: boolean;
  /** ACL-2: 'top_performer' | null. Set by generate-prompt-suggestions when an ungated insight was injected. */
  source?: string | null;
  expires_at?: string | null;
}

export class AISuggestionsService extends BaseService {
  private static instance: AISuggestionsService;

  private constructor() {
    super({
      defaultTTL: 5 * 60 * 1000,
      enableCache: false,
      enableRetry: true,
    });
  }

  public static getInstance(): AISuggestionsService {
    if (!AISuggestionsService.instance) {
      AISuggestionsService.instance = new AISuggestionsService();
    }
    return AISuggestionsService.instance;
  }

  async getUnusedSuggestions(
    userId: string,
    count: number,
    brandKitId?: string | null,
  ): Promise<ServiceResponse<AISuggestion[]>> {
    return this.execute(async () => {
      let query = this.client
        .from('ai_prompt_suggestions')
        .select('*')
        .eq('user_id', userId)
        .eq('is_used', false)
        .gt('expires_at', new Date().toISOString());

      if (brandKitId) query = query.eq('brand_kit_id', brandKitId);

      const { data, error } = await query.order('created_at', { ascending: false }).limit(count);

      if (error) throw error;

      return (data as AISuggestion[]) || [];
    });
  }

  async markUsed(id: string): Promise<ServiceResponse<void>> {
    return this.execute(async () => {
      const { error } = await this.client
        .from('ai_prompt_suggestions')
        .update({ is_used: true })
        .eq('id', id);

      if (error) throw error;
    });
  }
}

export const aiSuggestionsService = AISuggestionsService.getInstance();
